import { registerPlugin } from '@capacitor/core';

// ===== DATA TYPES =====

export interface CachedSnippet {
  id: string;
  note_id: string;
  user_id: string;
  version: number;
  title: string;
  content: string;
  favorite: boolean;
  upvotes: number;
  downvotes: number;
  created_at: string;
  updated_at: string;
  note_tags: string[];
  note_title: string;
}

export interface CachedNote {
  id: string;
  user_id: string;
  title: string;
  tags: string[];
  updated_at: string;
  pinned: boolean;
}

// ===== SIGNAL TYPES =====

export type SignalAction =
  | 'toggleFavorite'
  | 'upvote'
  | 'downvote'
  | 'recordUsage'
  | 'requestSync'
  | 'createSnippet';

export interface KeyboardSignal {
  id: string;
  action: SignalAction;
  snippetId?: string;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

// ===== PLUGIN INTERFACE =====

export interface PromptNotePluginInterface {
  // ===== AUTH =====
  setAuthToken(options: {
    token: string;
    refreshToken: string;
    userId?: string;
  }): Promise<void>;

  getAuthToken(): Promise<{
    token: string | null;
    refreshToken: string | null;
  }>;

  clearAuth(): Promise<void>;

  // ===== EXPORT (WebView -> Keyboard) =====
  exportSnippets(options: {
    snippets: CachedSnippet[];
  }): Promise<{ count: number }>;

  exportNotes(options: {
    notes: CachedNote[];
  }): Promise<{ count: number }>;

  clearCache(): Promise<void>;

  // ===== SIGNAL QUEUE (Keyboard -> WebView) =====
  getPendingSignals(): Promise<{
    signals: KeyboardSignal[];
  }>;

  clearSignals(options: {
    signalIds: string[];
  }): Promise<void>;

  // ===== DEEP LINKS =====
  openKeyboardSettings(): Promise<void>;
  openMainApp(options?: { route?: string }): Promise<void>;

  // ===== EVENTS =====
  addListener(
    eventName: 'signalsPending',
    callback: (data: { count: number }) => void
  ): Promise<{ remove: () => void }>;

  addListener(
    eventName: 'snippetInserted',
    callback: (data: { snippetId: string; timestamp: number }) => void
  ): Promise<{ remove: () => void }>;
}

// Register the plugin
const PromptNote = registerPlugin<PromptNotePluginInterface>('PromptNote');

export default PromptNote;
